import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileText } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScheduleForm, DEFAULT_SCHEDULE, toSchedulePayload } from "@/components/reports/ScheduleForm";
import { REPORTS } from "@/constants/testIds";
import { detectTimezone } from "@/lib/cron";
import api from "@/lib/api";

export function SaveReportDialog({ open, onOpenChange, prompt, plan, chartType, workspaceId }) {
  const navigate = useNavigate();
  const [name, setName] = useState((prompt || "Untitled report").slice(0, 120));
  const [scheduled, setScheduled] = useState(false);
  const [schedule, setSchedule] = useState({ ...DEFAULT_SCHEDULE, timezone: detectTimezone() });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || !workspaceId) return;
    setSaving(true);
    try {
      const body = {
        workspace_id: workspaceId,
        name: name.trim(),
        prompt: prompt || "",
        plan_frozen: plan || {},
        chart_type: chartType || null,
      };
      if (scheduled) body.schedule = toSchedulePayload(schedule);
      const { data } = await api.post("/reports", body);
      toast.success("Report saved", {
        description: scheduled ? "Schedule created — it'll run automatically." : "Saved to Reports.",
        action: { label: "Open", onClick: () => navigate(`/reports/${data.id}`) },
      });
      onOpenChange(false);
    } catch (e) {
      toast.error("Couldn't save report", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={REPORTS.saveDialog} className="max-h-[90vh] overflow-y-auto font-mono sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-accent" /> Save as report
          </DialogTitle>
          <DialogDescription className="text-xs">
            Freezes this answer's SQL so future runs use the same query — even if the glossary changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Report name</Label>
            <Input
              data-testid={REPORTS.saveName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-card/60"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
            <div>
              <p className="text-sm">Schedule recurring delivery</p>
              <p className="text-xs text-muted-foreground">Auto-send this report to email, Slack, or a webhook.</p>
            </div>
            <Switch data-testid={REPORTS.saveScheduleToggle} checked={scheduled} onCheckedChange={setScheduled} />
          </div>

          {scheduled && <ScheduleForm value={schedule} onChange={setSchedule} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button
            data-testid={REPORTS.saveSubmit}
            onClick={save}
            disabled={saving || !name.trim()}
            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
